import { bootstrapApplication } from '@angular/platform-browser';
import { Component, provideZoneChangeDetection, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule, Routes, withInMemoryScrolling } from '@angular/router';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';

// Register German locale data
registerLocaleData(localeDe);

import { LayoutComponent } from './app/components/layout/layout.component';
import { LoginComponent } from './app/views/login/login.component';
import { NotFoundComponent } from './app/views/not-found/not-found.component';
import { RedeemComponent } from './app/views/redeem/redeem.component';
import { RegisterComponent } from './app/views/register/register.component';
import { RegisterSuccessComponent } from './app/views/register-success/register-success.component';
import { OnboardingComponent } from './app/views/onboarding/onboarding.component';
import { VerifyEmailComponent } from './app/views/verify-email/verify-email.component';
import { SettingsComponent } from './app/components/settings/settings.component';
import { FinancialDashboardComponent } from './app/components/financial-dashboard/financial-dashboard.component';
import { MemberPayoutsComponent } from './app/views/member-payouts/member-payouts.component';
import { VoucherDetailsComponent } from './app/components/voucher-details/voucher-details.component';
import { VoucherDashboardComponent } from './app/views/voucher-dashboard/voucher-dashboard.component';
import { RootRouterComponent } from './app/components/root-router/root-router.component';
import { VoucherVerificationComponent } from './app/components/voucher-verification/voucher-verification.component';
import { ShopMapComponent } from './app/views/shop-map/shop-map.component';
import { ProductsComponent } from './app/views/products/products.component';
import { VoucherDesignEditorComponent } from './app/views/voucher-design-editor/voucher-design-editor.component';
import { unsavedChangesGuard } from './app/views/voucher-design-editor/voucher-design-editor.guard';

import { GroupManagementComponent } from './app/views/group-management/group-management.component';
import { JoinGroupComponent } from './app/views/join-group/join-group.component';
import { ConfirmIbanComponent } from './app/views/confirm-iban/confirm-iban.component';
import { AffiliateComponent } from './app/views/affiliate/affiliate.component';
import { partnerAuthInterceptor } from './app/services/partner-auth.interceptor';
import { PartnerLoginComponent } from './app/views/partner-login/partner-login.component';
import { PartnerSetPasswordComponent } from './app/views/partner-set-password/partner-set-password.component';
import { PartnerAreaComponent } from './app/views/partner-area/partner-area.component';
import { partnerGuard } from './app/guards/partner.guard';
import { PartnerVerifyEmailComponent } from './app/views/partner-verify-email/partner-verify-email.component';
import { ClaimAccountComponent } from './app/views/claim-account/claim-account.component';

import { authGuard } from './app/guards/auth.guard';
import { onboardingGuard } from './app/guards/onboarding.guard';
import { memberGuard, notMemberGuard } from './app/guards/member.guard';
import { ForgotPasswordComponent } from './app/views/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './app/views/reset-password/reset-password.component';
import { VerifyNewEmailComponent } from './app/views/verify-new-email/verify-new-email.component';
import {
  authInterceptor,
  AUTH_INTERCEPTOR_SERVICE,
  createAuthInterceptorService,
} from './app/services/auth.interceptor';
import { StorefrontService } from './app/services/storefront.service';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { environment } from './environments/environment';
import { buildInfo } from './build-info';
import { TokenStorageService } from './app/services/token-storage.service';
import { LoggerService } from './app/services/logger.service';
import { AppUpdateService } from './app/services/app-update.service';
import { provideServiceWorker } from '@angular/service-worker';
import { ApplicationRef } from '@angular/core';
import { isDevMode } from '@angular/core';
import { of, switchMap } from 'rxjs';

// Initialize logger for main.ts (no DI available here)
const logger = new LoggerService();

// 🚀 [ENVIRONMENT] Log environment configuration at startup
logger.log('🚀 [ENVIRONMENT] Application starting with environment configuration:');
logger.log('⏰ [BUILD] Build info:', buildInfo);
logger.log('📋 [ENVIRONMENT] Full environment object:', environment);
logger.log('🌐 [ENVIRONMENT] Key values:', {
  env: environment.env,
  dashboardUrl: environment.dashboardUrl,
  storefrontBaseDomain: environment.storefrontBaseDomain,
  apiUrl: environment.apiUrl,
  frontendUrl: environment.frontendUrl,
  voucherScriptUrl: environment.voucherScriptUrl,
  currentURL: window.location.href,
  hostname: window.location.hostname,
});
logger.log('🔍 [ENVIRONMENT] Debug checks:', {
  Environment: environment.env,
  'Is dashboardUrl localhost?': environment.dashboardUrl.includes('localhost'),
  'Is storefrontBaseDomain localhost?': environment.storefrontBaseDomain.includes('localhost'),
  'Current hostname': window.location.hostname,
  'Should be detected as storefront?': window.location.hostname !== environment.dashboardUrl,
});

// Make token debug functions globally available for troubleshooting
const tokenStorage = new (class {
  private service: TokenStorageService | null = null;

  init() {
    // Lazy initialization to avoid circular dependencies
    if (!this.service) {
      this.service = new TokenStorageService(logger);
    }
    return this.service;
  }

  debug() {
    this.init().debugShowAllTokens();
  }

  clearCurrent() {
    this.init().clearAll();
    logger.log(`✅ Cleared tokens for current environment: ${environment.env}`);
  }

  clearAll() {
    const environments = ['dev', 'qa', 'prod'];
    environments.forEach((env) => {
      const keys = [
        'access_token',
        'public_company_id',
        'company_name',
        'company_stripe_account_id',
        'company_is_stripe_onboarding_success',
        'company_is_profile_completed',
      ];
      keys.forEach((key) => {
        localStorage.removeItem(`schenkwerk_${env}_${key}`);
      });
    });
    logger.log('✅ Cleared ALL tokens from ALL environments');
  }
})();

// Make globally available for debugging
(window as any).schenkwerkTokens = tokenStorage;
// Show current token debug info on startup
tokenStorage.debug();

logger.log('🔧 [DEBUG] Token management available at: window.schenkwerkTokens');
logger.log('📋 [DEBUG] Available commands:');
logger.log('  - window.schenkwerkTokens.debug() - Show all tokens');
logger.log('  - window.schenkwerkTokens.clearCurrent() - Clear current env tokens');
logger.log('  - window.schenkwerkTokens.clearAll() - Clear ALL tokens');

const routes: Routes = [
  {
    path: 'groups',
    component: GroupManagementComponent,
    canActivate: [authGuard, onboardingGuard],
  },
  // Old invite links: forwards logged-out visitors to the member registration.
  { path: 'join-group', component: JoinGroupComponent },
  // Link from the "Neue IBAN bestätigen" e-mail; no guard, the token is the proof.
  { path: 'confirm-iban', component: ConfirmIbanComponent },

  {
    path: 'voucher-dashboard',
    component: VoucherDashboardComponent,
    canActivate: [authGuard, onboardingGuard],
  },
  { path: 'register', component: RegisterComponent },
  // Public affiliate sign-up; affiliates share /register?ref=CODE.
  { path: 'partner', component: AffiliateComponent },
  // The partner area: own login (password set through a mailed link), own session.
  { path: 'partner/login', component: PartnerLoginComponent },
  { path: 'partner/set-password', component: PartnerSetPasswordComponent },
  { path: 'partner/verify-email', component: PartnerVerifyEmailComponent },
  { path: 'partner/clients', component: PartnerAreaComponent, canActivate: [partnerGuard] },
  // From the mail to a customer whose agency created the account; the token is the proof.
  { path: 'claim', component: ClaimAccountComponent },
  // A shop prepared in ChatGPT: its preview, and taking it over (src/app/services/shop-draft)
  {
    path: 'shop-draft/claim',
    loadComponent: () =>
      import('./app/views/shop-draft-claim/shop-draft-claim.component').then(
        (m) => m.ShopDraftClaimComponent,
      ),
  },
  {
    path: 'shop-draft/preview/:reference',
    loadComponent: () =>
      import('./app/views/shop-draft-preview/shop-draft-preview.component').then(
        (m) => m.ShopDraftPreviewComponent,
      ),
  },
  { path: 'welcome', component: RegisterSuccessComponent },
  { path: 'register-success', component: RegisterSuccessComponent },
  { path: 'verify-email', component: VerifyEmailComponent },
  { path: 'login', component: LoginComponent },
  {
    path: '',
    component: RootRouterComponent,
    canActivate: [
      // Only need guards for dashboard, skip for storefronts and gutscheine
      () => {
        logger.log('🛡️ [RouteGuard] Root route guard executing');
        const storefrontService = inject(StorefrontService);
        const isStorefront = storefrontService.isStorefront();
        const isGutscheineSubdomain = storefrontService.isSubdomain('gutscheine');

        logger.log('🎯 [RouteGuard] Route decision:', {
          isStorefront,
          isGutscheineSubdomain,
          action:
            isStorefront || isGutscheineSubdomain
              ? 'SKIP_AUTH (public page)'
              : 'REQUIRE_AUTH (dashboard)',
        });

        if (isStorefront) {
          logger.log('✅ [RouteGuard] Allowing public access to storefront');
          return true; // Skip guards for storefronts
        }

        if (isGutscheineSubdomain) {
          logger.log('✅ [RouteGuard] Allowing public access to gutscheine subdomain');
          return true; // Skip guards for gutscheine subdomain
        }

        logger.log('🔒 [RouteGuard] This is dashboard, will be checked by individual guards');
        // authGuard returns an Observable, so `authGuard() && onboardingGuard()` would never
        // check authentication at all. Chain them: onboarding only once auth passed.
        // onboardingGuard calls inject(), which only works synchronously inside the guard,
        // so it has to run in the captured injection context once auth resolves.
        const injector = inject(EnvironmentInjector);
        return authGuard().pipe(
          switchMap((isAuthenticated) =>
            isAuthenticated ? runInInjectionContext(injector, onboardingGuard) : of(false),
          ),
        );
      },
    ],
  },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'verify-new-email', component: VerifyNewEmailComponent },
  { path: 'onboarding', component: OnboardingComponent, canActivate: [authGuard] },
  { path: 'settings', component: SettingsComponent, canActivate: [authGuard, onboardingGuard] },
  {
    path: 'voucher-designs/:id',
    component: VoucherDesignEditorComponent,
    canActivate: [authGuard, onboardingGuard, notMemberGuard],
    canDeactivate: [unsavedChangesGuard],
  },
  {
    path: 'financial-dashboard',
    component: FinancialDashboardComponent,
    canActivate: [authGuard, onboardingGuard, notMemberGuard],
  },
  {
    path: 'payouts',
    component: MemberPayoutsComponent,
    canActivate: [authGuard, onboardingGuard, memberGuard],
  },
  { path: 'products', component: ProductsComponent, canActivate: [authGuard, onboardingGuard] },
  { path: 'redeem', component: RedeemComponent, canActivate: [authGuard, onboardingGuard] },
  { path: 'verify/:voucherCode', component: VoucherVerificationComponent },
  {
    path: 'in/:city',
    component: ShopMapComponent,
    canActivate: [
      () => {
        const storefrontService = inject(StorefrontService);
        const isGutscheineSubdomain = storefrontService.isSubdomain('gutscheine');

        logger.log('🗺️ [RouteGuard] Gutscheine route guard:', {
          isGutscheineSubdomain,
          hostname: window.location.hostname,
          action: isGutscheineSubdomain ? 'ALLOW' : 'DENY',
        });

        return isGutscheineSubdomain;
      },
    ],
  },
  { path: '**', component: NotFoundComponent },
];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule, LayoutComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="min-h-screen bg-gray-50">
      <app-layout></app-layout>
    </div>
  `,
})
export class App {}

bootstrapApplication(App, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        // Off: the pages with anchors (settings, products, the setup checklist)
        // scroll smoothly to the section and highlight it themselves. With the
        // router jumping there first, a link scrolled twice, up and down.
        anchorScrolling: 'disabled',
      }),
    ),
    provideHttpClient(withXhr(), withInterceptors([partnerAuthInterceptor, authInterceptor])),
    {
      provide: AUTH_INTERCEPTOR_SERVICE,
      useFactory: createAuthInterceptorService,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
})
  .then((appRef) => {
    appRef.injector.get(AppUpdateService).start();
  })
  .catch((err) => console.error('Application bootstrap failed:', err));
